import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  makeStyles,
  Fade,
  Paper,
  ClickAwayListener,
  CircularProgress,
  Tooltip,
  Portal
} from "@material-ui/core";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  reactionsContainer: {
    display: "flex",
    alignItems: "center",
    backgroundColor: theme.palette.type === "light" ? "#fff" : "#2a3942",
    borderRadius: "20px",
    padding: "0 6px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
  },
  reactionButton: {
    fontSize: "1.1rem",
    cursor: "pointer",
    margin: "1px",
    padding: "3px",
    borderRadius: "50%",
    transition: "transform 0.2s ease, background-color 0.2s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "36px",
    height: "36px",
    "&:hover": {
      backgroundColor: theme.palette.type === "light" ? "#f0f2f5" : "#384c56",
      transform: "scale(1.15)",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed"
    }
  },
  selectedReaction: {
    backgroundColor: theme.palette.primary.light,
  },
  loading: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "rgba(0, 0, 0, 0.3)",
    borderRadius: "20px",
    zIndex: 10006,
  }
}));

const defaultEmojis = ["👍", "❤️", "🤣", "🤝", "😬", "🙏"];

const MessageReactions = ({ message, onClose, ticketId, anchorEl }) => {
  const classes = useStyles();
  const isMounted = useRef(true);
  const [sendingReaction, setSendingReaction] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const maxRetries = 2;

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    let timeoutId;
    if (sendingReaction) {
      timeoutId = setTimeout(() => {
        if (isMounted.current) {
          setSendingReaction(false);
          setDisabled(false);
        }
      }, 20000);
    }
    return () => clearTimeout(timeoutId);
  }, [sendingReaction]);

  const sendReaction = useCallback(async (emoji) => {
    try {
      if (isMounted.current) {
        setSendingReaction(true);
        setDisabled(true);
      }

      const shouldRemove = message.reaction === emoji;

      await api.post(`/messages/${ticketId}/reaction`, {
        messageId: message.id,
        reaction: shouldRemove ? "" : emoji,
      });

      if (isMounted.current) {
        setSendingReaction(false);
        setDisabled(false);
        onClose();
      }

      return true;
    } catch (err) {
      console.error("Erro ao enviar reação:", err);

      if (isMounted.current) {
        if (retryCount < maxRetries) {
          setRetryCount(prev => prev + 1);
        } else {
          toastError(err);
        }

        setSendingReaction(false);
        setDisabled(false);
      }

      return false;
    }
  }, [message.id, ticketId, onClose, retryCount, maxRetries, message.reaction]);

  const handleEmojiClick = async (emoji) => {
    if (sendingReaction || disabled || !isMounted.current) return;

    await sendReaction(emoji);
  };

  const handleClickAway = () => {
    if (!sendingReaction) {
      onClose();
    }
  };

  const isCurrentReaction = (emoji) => {
    return message.reaction === emoji;
  };

  // Calcular posição baseada no anchorEl
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (anchorEl) {
      const updatePosition = () => {
        const rect = anchorEl.getBoundingClientRect();
        setPosition({
          top: rect.top - 50, // Posicionar acima do botão (fixed não precisa de scrollY)
          left: rect.left + (rect.width / 2) // Centralizar horizontalmente (fixed não precisa de scrollX)
        });
      };

      updatePosition();
      
      // Atualizar posição em caso de scroll ou resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [anchorEl]);

  if (!anchorEl) return null;

  return (
    <Portal>
      <ClickAwayListener onClickAway={handleClickAway}>
        <div
          style={{
            position: "fixed",
            top: `${position.top}px`,
            left: `${position.left}px`,
            transform: "translateX(-50%)",
            zIndex: 10005,
            pointerEvents: "auto"
          }}
        >
          <Fade in={true}>
            <Paper className={classes.reactionsContainer}>
              {sendingReaction && (
                <div className={classes.loading}>
                  <CircularProgress size={20} />
                </div>
              )}

              {defaultEmojis.map((emoji, index) => (
                <Tooltip
                  key={index}
                  title={isCurrentReaction(emoji) ? "Clique para remover" : ""}
                  placement="top"
                >
                  <div
                    className={`${classes.reactionButton} ${isCurrentReaction(emoji) ? classes.selectedReaction : ""}`}
                    onClick={() => handleEmojiClick(emoji)}
                    style={{ opacity: disabled ? 0.5 : 1, cursor: disabled ? "not-allowed" : "pointer" }}
                  >
                    {emoji}
                  </div>
                </Tooltip>
              ))}

            </Paper>
          </Fade>
        </div>
      </ClickAwayListener>
    </Portal>
  );
};

export default MessageReactions;
